import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TENANT } from '../../config';

/**
 * eMerge privacy policy.
 *
 * Separate from the shared PrivacyPolicy component because the shared one
 * describes a securities business — it collects investor accreditation and
 * suitability, and never mentions health information. Rendering that to a
 * patient would be inaccurate, and a privacy policy that misdescribes what is
 * collected is worse than none: it is a public statement people rely on.
 *
 * This page also serves a second purpose. Plaid Identity Verification requires
 * a reachable privacy policy URL describing how verification data is handled,
 * and reviews it. The Plaid section below is written to that requirement.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * BEFORE THIS GOES LIVE — three values must be supplied by the business, and
 * are deliberately left as visible placeholders rather than invented. Every
 * other statement on this page describes something the platform verifiably
 * does today.
 *
 *   PRIVACY_OFFICER_EMAIL  who receives privacy requests
 *   POSTAL_ADDRESS         required for a HIPAA notice
 *   RETENTION_SUMMARY      the retention schedule, which is still undocumented
 *
 * A lawyer should review this before publication. It is written to be accurate
 * about system behaviour, which is the part engineering can answer; whether it
 * is sufficient as a HIPAA Notice of Privacy Practices is a question for
 * counsel.
 * ─────────────────────────────────────────────────────────────────────────
 */

const PRIVACY_OFFICER_EMAIL = 'privacy@emergesc.com'; // TODO confirm this mailbox exists
const POSTAL_ADDRESS = '[POSTAL ADDRESS — to be supplied]';
const RETENTION_SUMMARY = '[RETENTION SCHEDULE — to be confirmed with compliance]';
const EFFECTIVE_DATE = 'August 23, 2026';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-neutral-900 mb-3">{title}</h2>
    <div className="space-y-3 text-neutral-700 leading-relaxed">{children}</div>
  </section>
);

const EmergePrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const platformName = TENANT.branding.name;

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-neutral-200 p-8 md:p-12">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2">Privacy Policy</h1>
          <p className="text-neutral-500 mb-10">Effective {EFFECTIVE_DATE}</p>

          <Section title="Who we are">
            <p>
              {platformName} provides hearing care and related health services to residents of
              skilled nursing and senior living facilities, and to the facilities that care for
              them. This policy explains what information we collect, why, and what you can ask
              us to do with it.
            </p>
            <p>
              We handle health information. Where we do, we act as a HIPAA covered entity or as a
              business associate of the facility providing care, and we handle protected health
              information under the standards HIPAA requires.
            </p>
          </Section>

          <Section title="What we collect">
            <p><strong>Health and care information.</strong> Records relating to hearing care —
              assessments, devices supplied, appointment and visit history, the facility and
              provider involved, and funding or coverage status for the care delivered.</p>
            <p><strong>Contact and demographic information.</strong> Name, date of birth, contact
              details, responsible party or representative, and the facility you reside in.</p>
            <p><strong>Identity verification information.</strong> Where identity must be
              confirmed, the details described in the next section.</p>
            <p><strong>Technical information.</strong> When you use our software we record access
              logs — the address you connected from, which page or endpoint was requested, the
              time, and the outcome. These logs are kept for security purposes.
              We deliberately do not record the contents of what you type into search fields, so
              a person's name entered into a search does not appear in a security log.</p>
          </Section>

          <Section title="Identity verification through Plaid">
            <p>
              To confirm identity we use <strong>Plaid Inc.</strong> ("Plaid"). When you complete
              identity verification, information is collected and processed by Plaid on our
              behalf. Depending on the check performed, this can include your name, date of
              birth, residential address, phone number, an image of a government-issued
              identification document, and a photograph of your face taken at the time of
              verification for comparison against that document.
            </p>
            <p>
              That information is transmitted directly to Plaid and processed by Plaid under its
              own privacy policy. We receive the <em>outcome</em> of the verification and the
              limited details needed to record that it took place. We do not receive or store
              your identification document images.
            </p>
            <p>
              Plaid's handling of this information is described at{' '}
              <a
                href="https://plaid.com/legal/#end-user-privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 underline"
              >
                plaid.com/legal
              </a>. Identity verification is used to confirm identity and to meet legal and
              anti-fraud obligations. It is not used for advertising, and it is not sold.
            </p>
          </Section>

          <Section title="How we use information">
            <p>To deliver and coordinate care; to communicate with you, your representative and
              your facility; to verify identity; to bill and to establish coverage or funding;
              to meet legal, regulatory and accreditation obligations; and to keep our systems
              secure and functioning.</p>
            <p><strong>We do not sell personal information, and we do not sell or share health
              information for advertising.</strong></p>
          </Section>

          <Section title="Who we share it with">
            <p><strong>Your care.</strong> The facility where you reside, treating providers, and
              people you have authorised to act for you.</p>
            <p><strong>Payers.</strong> Insurers and programmes that cover the care provided,
              where needed to establish coverage or obtain payment.</p>
            <p><strong>Service providers.</strong> Companies that run parts of our operation under
              contract — identity verification (Plaid), cloud hosting and storage, communications,
              and electronic signature. They may use the information only to perform that service
              for us. Where they handle protected health information, they do so under a business
              associate agreement.</p>
            <p><strong>When the law requires it.</strong> In response to lawful requests, or where
              disclosure is required or permitted by law.</p>
          </Section>

          <Section title="How we protect it">
            <p>Information is encrypted in transit and at rest. Access is restricted to people
              whose role requires it, and access to health information is recorded. Documents and
              backups are held in encrypted storage that is not publicly reachable.</p>
            <p>No system is perfectly secure, and we will not claim otherwise. If a breach affects
              your information we will notify you as the law requires.</p>
          </Section>

          <Section title="How long we keep it">
            <p>{RETENTION_SUMMARY}</p>
            <p>Security access logs are kept for 30 days. Records we are required to retain by law
              or by our professional obligations are kept for the period those rules require.</p>
          </Section>

          <Section title="Your rights">
            <p>Depending on where you live and the type of information involved, you may ask us to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>give you a copy of the information we hold about you;</li>
              <li>correct information that is wrong or incomplete;</li>
              <li>tell you who we have disclosed your health information to;</li>
              <li>restrict certain uses or disclosures;</li>
              <li>communicate with you by a particular method or at a particular address;</li>
              <li>delete information, where we are not required to keep it.</li>
            </ul>
            <p>
              To make a request, contact{' '}
              <a href={`mailto:${PRIVACY_OFFICER_EMAIL}`} className="text-emerald-700 underline">
                {PRIVACY_OFFICER_EMAIL}
              </a>. We will not treat you differently for exercising these rights.
            </p>
          </Section>

          <Section title="Contact us">
            <p>
              Privacy Officer<br />
              {platformName}<br />
              {POSTAL_ADDRESS}<br />
              <a href={`mailto:${PRIVACY_OFFICER_EMAIL}`} className="text-emerald-700 underline">
                {PRIVACY_OFFICER_EMAIL}
              </a>
            </p>
            <p>
              If you believe your privacy rights have been violated you may complain to us, or to
              the U.S. Department of Health and Human Services Office for Civil Rights. We will
              not retaliate against you for filing a complaint.
            </p>
          </Section>

          <Section title="Changes to this policy">
            <p>If we change this policy we will update the effective date above and, where the
              change is significant, tell you directly.</p>
          </Section>

          <p className="text-sm text-neutral-500 pt-6 border-t border-neutral-200">
            <Link to="/terms" className="text-emerald-700 underline">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmergePrivacyPolicy;
